import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, GitBranch, Plus, Globe, Users, TrendingUp,
  ChevronRight, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, AlertCircle, Clock,
  Percent, DollarSign, Factory, Trash2, Edit, Save, X, HelpCircle, 
  FileSpreadsheet, BarChart3, Target, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { apiFetch } from '../services/api_service';


const API_URL = process.env.REACT_APP_BACKEND_URL;

const METHODOLOGIES = [
  { 
    id: 'equity_share', 
    name: 'Equity Share', 
    description: 'Report emissions proportional to ownership percentage',
    icon: '📊'
  },
  { 
    id: 'operational_control', 
    name: 'Operational Control', 
    description: 'Report 100% of emissions from operationally controlled entities',
    icon: '🏭'
  },
  { 
    id: 'financial_control', 
    name: 'Financial Control', 
    description: 'Report 100% of emissions from financially controlled entities',
    icon: '💰'
  }
];

const ENTITY_TYPES = [
  { id: 'subsidiary', name: 'Subsidiary' },
  { id: 'joint_venture', name: 'Joint Venture' },
  { id: 'associate', name: 'Associate' },
  { id: 'investment', name: 'Investment' }
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Germany', 'France', 'Netherlands',
  'Belgium', 'Spain', 'Italy', 'Poland', 'Sweden', 'Denmark', 'Finland',
  'Norway', 'Switzerland', 'Austria', 'Ireland', 'Portugal', 'Czech Republic',
  'Hungary', 'Romania', 'China', 'Japan', 'South Korea', 'India', 'Australia',
  'Brazil', 'Mexico', 'Canada', 'Singapore', 'UAE'
];

// Methodology Selector Component
const MethodologySelector = ({ selected, onSelect }) => {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {METHODOLOGIES.map(method => (
        <Card 
          key={method.id}
          className={`cursor-pointer transition-all hover:shadow-md ${
            selected === method.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
          }`}
          onClick={() => onSelect(method.id)}
          data-testid={`methodology-${method.id}`}
        >
          <CardContent className="pt-6">
            <div className="text-3xl mb-3">{method.icon}</div>
            <h4 className="font-semibold">{method.name}</h4>
            <p className="text-sm text-gray-500 mt-2">{method.description}</p>
            {selected === method.id && (
              <Badge className="mt-3 bg-blue-500">Selected</Badge>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Entity Form Component
const EntityForm = ({ entity, onSave, onCancel }) => {
  const [formData, setFormData] = useState(entity || {
    name: '',
    entity_type: 'subsidiary',
    country: 'United States',
    ownership_percentage: 100,
    has_operational_control: false,
    has_financial_control: false,
    sector: '',
    employees: '',
    revenue: ''
  });

  return (
    <Card className="border-2 border-blue-200 bg-blue-50">
      <CardHeader>
        <CardTitle className="text-lg">
          {entity ? 'Edit Entity' : 'Add New Entity'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Entity Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Acme Corp Germany"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Entity Type *</label>
            <Select 
              value={formData.entity_type}
              onValueChange={(v) => setFormData({...formData, entity_type: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Country *</label>
            <Select 
              value={formData.country}
              onValueChange={(v) => setFormData({...formData, country: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Ownership % *</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.ownership_percentage}
              onChange={(e) => setFormData({...formData, ownership_percentage: parseFloat(e.target.value) || 0})}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Sector</label>
            <Input
              value={formData.sector}
              onChange={(e) => setFormData({...formData, sector: e.target.value})}
              placeholder="e.g., Manufacturing"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Employees</label>
            <Input
              type="number"
              value={formData.employees}
              onChange={(e) => setFormData({...formData, employees: parseInt(e.target.value) || ''})}
              placeholder="Number of employees"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Revenue (€ millions)</label>
            <Input
              type="number"
              value={formData.revenue}
              onChange={(e) => setFormData({...formData, revenue: parseFloat(e.target.value) || ''})}
              placeholder="Annual revenue"
            />
          </div>
          <div className="space-y-2 pt-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.has_operational_control}
                onChange={(e) => setFormData({...formData, has_operational_control: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Operational Control</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.has_financial_control}
                onChange={(e) => setFormData({...formData, has_financial_control: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm">Financial Control</span>
            </label>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-4">
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={() => onSave(formData)} disabled={!formData.name}>
            <Save className="h-4 w-4 mr-2" />
            {entity ? 'Update' : 'Add Entity'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Group Structure Setup Component
const GroupStructureSetup = ({ onStructureCreated }) => {
  const [step, setStep] = useState(1);
  const [parentName, setParentName] = useState('');
  const [parentCountry, setParentCountry] = useState('United States');
  const [methodology, setMethodology] = useState('');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear());
  const [entities, setEntities] = useState([]);
  const [showEntityForm, setShowEntityForm] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleAddEntity = (entity) => {
    setEntities([...entities, { ...entity, entity_id: `entity_${Date.now()}` }]);
    setShowEntityForm(false);
    toast.success('Entity added');
  };

  const handleRemoveEntity = (index) => {
    setEntities(entities.filter((_, i) => i !== index));
  };

  const handleCreateStructure = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/group/structures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_name: parentName,
          parent_country: parentCountry,
          control_methodology: methodology,
          reporting_year: reportingYear,
          entities
        })
      });

      if (response && !response._error) {
        toast.success('Group structure created successfully');
        onStructureCreated(response);
      } else {
        toast.error(response?.message || 'Failed to create structure');
      }
    } catch (error) {
      toast.error('Error creating group structure');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-4 mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              step >= s ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
            </div>
            {s < 4 && (
              <div className={`w-16 h-1 ${step > s ? 'bg-blue-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Parent Company */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Parent Company</CardTitle>
            <CardDescription>Define your group's parent company</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Parent Company Name *</label>
                <Input
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  placeholder="e.g., Acme Corporation"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Headquarters Country *</label>
                <Select value={parentCountry} onValueChange={setParentCountry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Reporting Year</label>
              <Input
                type="number"
                value={reportingYear}
                onChange={(e) => setReportingYear(parseInt(e.target.value))}
                min="2020"
                max="2030"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!parentName}>
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Methodology */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 2: Control Methodology</CardTitle>
            <CardDescription>Choose how to consolidate emissions across your group</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <MethodologySelector selected={methodology} onSelect={setMethodology} />
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)} disabled={!methodology}>
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Entities */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 3: Add Entities</CardTitle>
            <CardDescription>Add subsidiaries, joint ventures, and associates</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {entities.length > 0 && (
              <div className="space-y-3">
                {entities.map((entity, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <Building2 className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{entity.name}</p>
                        <p className="text-sm text-gray-500">
                          {entity.entity_type} • {entity.country} • {entity.ownership_percentage}%
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveEntity(idx)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {showEntityForm ? (
              <EntityForm onSave={handleAddEntity} onCancel={() => setShowEntityForm(false)} />
            ) : (
              <Button variant="outline" className="w-full" onClick={() => setShowEntityForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Entity
              </Button>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={() => setStep(4)} disabled={entities.length === 0}>
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & Create */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 4: Review & Create</CardTitle>
            <CardDescription>Review your group structure before creating</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Parent Company</p>
                <p className="font-semibold text-lg">{parentName}</p>
                <p className="text-sm text-gray-500">{parentCountry}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Consolidation Methodology</p>
                <p className="font-semibold text-lg">
                  {METHODOLOGIES.find(m => m.id === methodology)?.name}
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3">Entities ({entities.length})</h4>
              <div className="space-y-2">
                {entities.map((entity, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                    <span>{entity.name}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{entity.entity_type}</Badge>
                      <Badge variant="secondary">{entity.ownership_percentage}%</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
              <Button onClick={handleCreateStructure} disabled={creating}>
                {creating ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Create Group Structure
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Consolidation Results Component
const ConsolidationResults = ({ results }) => {
  if (!results) return null;

  const formatNumber = (n) => n?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0';

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Scope 1</p>
            <p className="text-2xl font-bold text-blue-600">{formatNumber(results.total_scope1)}</p>
            <p className="text-xs text-gray-400">tCO₂e</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Scope 2 (Market)</p>
            <p className="text-2xl font-bold text-green-600">{formatNumber(results.total_scope2_market)}</p>
            <p className="text-xs text-gray-400">tCO₂e</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Scope 3</p>
            <p className="text-2xl font-bold text-purple-600">{formatNumber(results.total_scope3)}</p>
            <p className="text-xs text-gray-400">tCO₂e</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Total Emissions</p>
            <p className="text-2xl font-bold">{formatNumber(results.total_emissions)}</p>
            <p className="text-xs text-gray-400">tCO₂e</p>
          </CardContent>
        </Card>
      </div>

      {/* Coverage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Data Coverage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500"
                  style={{ width: `${results.data_coverage_percentage || 0}%` }}
                />
              </div>
            </div>
            <span className="font-bold text-green-600">{results.data_coverage_percentage}%</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {results.entities_reporting} of {results.entities_total} entities reporting
          </p>
        </CardContent>
      </Card>

      {/* Breakdown by Entity */}
      {results.emissions_by_entity?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Emissions by Entity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.emissions_by_entity.map((entity, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{entity.name}</p>
                        <p className="text-xs text-gray-500">{entity.country}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatNumber(entity.scope1 + entity.scope2_market + entity.scope3)} tCO₂e</p>
                      <p className="text-xs text-gray-500">{entity.consolidation_percentage}% consolidated</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-blue-600">S1: {formatNumber(entity.scope1)}</span>
                    <span className="text-green-600">S2: {formatNumber(entity.scope2_market)}</span>
                    <span className="text-purple-600">S3: {formatNumber(entity.scope3)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Methodology Notes */}
      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-gray-500">
            <strong>Methodology:</strong> {results.methodology_notes}
          </p>
          {results.intercompany_eliminated > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              <strong>Intercompany Eliminated:</strong> {formatNumber(results.intercompany_eliminated)} tCO₂e
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Component
const GroupConsolidation = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [consolidationResults, setConsolidationResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consolidating, setConsolidating] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/group/structures`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleConsolidate = async () => {
    if (!selectedGroup) return;
    
    setConsolidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/group/${selectedGroup.group_id}/consolidate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            reporting_year: selectedGroup.reporting_year,
            include_intercompany_elimination: true
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConsolidationResults(data);
        toast.success('Consolidation complete');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Consolidation failed');
      }
    } catch (error) {
      toast.error('Error during consolidation');
    } finally {
      setConsolidating(false);
    }
  };

  const [showGuide, setShowGuide] = useState(true);

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="group-consolidation">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <GitBranch className="h-8 w-8 text-blue-600" />
          Group Consolidation
        </h1>
        <p className="text-gray-500 mt-1">
          Multi-entity ESG reporting with subsidiary rollup
        </p>
      </div>

      {/* In-Page Guide */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl mb-6 overflow-hidden">
        <button 
          onClick={() => setShowGuide(!showGuide)}
          className="w-full flex items-center justify-between p-4 hover:bg-blue-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <HelpCircle className="h-5 w-5 text-blue-600" />
            </div>
            <span className="font-semibold text-blue-900">How to Use Group Consolidation</span>
          </div>
          {showGuide ? <ChevronUp className="h-5 w-5 text-blue-600" /> : <ChevronDown className="h-5 w-5 text-blue-600" />}
        </button>
        
        {showGuide && (
          <div className="px-4 pb-4 space-y-4">
            {/* Quick Steps */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 text-blue-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold">1</span>
                  Create Structure
                </div>
                <p className="text-sm text-gray-600">Define your parent company and add subsidiaries, JVs, or associates with ownership %.</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 text-blue-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold">2</span>
                  Choose Methodology
                </div>
                <p className="text-sm text-gray-600">Select Equity Share, Operational, or Financial Control per GHG Protocol standards.</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 text-blue-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold">3</span>
                  Submit Entity Data
                </div>
                <p className="text-sm text-gray-600">Each subsidiary submits Scope 1, 2, 3 emissions, energy, water, and waste data.</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 text-blue-600 font-medium mb-1">
                  <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold">4</span>
                  Run Consolidation
                </div>
                <p className="text-sm text-gray-600">Get aggregated group emissions with proper accounting & intercompany elimination.</p>
              </div>
            </div>

            {/* Methodology Explainer */}
            <div className="bg-white rounded-lg border border-blue-200 p-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-600" />
                Consolidation Methodologies Explained
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-800">📊 Equity Share</p>
                  <p className="text-gray-600 mt-1">Report emissions proportional to ownership (60% owned = 60% of emissions reported)</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="font-medium text-green-800">🏭 Operational Control</p>
                  <p className="text-gray-600 mt-1">Report 100% of emissions from entities you operationally control</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="font-medium text-purple-800">💰 Financial Control</p>
                  <p className="text-gray-600 mt-1">Report 100% of emissions from entities you financially control</p>
                </div>
              </div>
            </div>

            {/* Data Flow Info */}
            <div className="bg-white rounded-lg border border-blue-200 p-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                Where Your Consolidated Data Flows
              </h4>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="bg-slate-50">📊 Dashboard - Group metrics</Badge>
                <Badge variant="outline" className="bg-slate-50">📤 Export Center - PDF/Excel reports</Badge>
                <Badge variant="outline" className="bg-slate-50">📋 CSRD Compliance - ESRS disclosures</Badge>
                <Badge variant="outline" className="bg-slate-50">🔮 Predictive Analytics - Forecasting</Badge>
                <Badge variant="outline" className="bg-slate-50">📈 Custom Dashboards - Widgets</Badge>
                <Badge variant="outline" className="bg-slate-50">🎯 Impact Engine - Footprint calcs</Badge>
                <Badge variant="outline" className="bg-slate-50">📁 XBRL Export - Digital submissions</Badge>
              </div>
            </div>

            {/* Pro Tips */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>💡 Pro Tips:</strong> Mark operational/financial control flags correctly — they determine consolidation treatment. 
                Check data coverage % before finalizing reports. The system auto-eliminates ~5% of Scope 3 Category 1 for intra-group transactions.
              </p>
            </div>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" className="gap-2">
            <Building2 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="setup" className="gap-2">
            <Plus className="h-4 w-4" />
            New Structure
          </TabsTrigger>
          <TabsTrigger value="consolidate" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Consolidate
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : groups.length > 0 ? (
            <div className="space-y-4">
              {groups.map((group, idx) => (
                <Card key={idx} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{group.parent_name}</h3>
                          <p className="text-sm text-gray-500">{group.parent_country}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">{group.control_methodology?.replace(/_/g, ' ')}</Badge>
                            <Badge variant="secondary">{group.total_entities} entities</Badge>
                            <Badge variant="secondary">{group.reporting_year}</Badge>
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setSelectedGroup(group);
                          setActiveTab('consolidate');
                        }}
                      >
                        Consolidate
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <GitBranch className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No group structures created yet</p>
                <Button className="mt-4" onClick={() => setActiveTab('setup')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Group Structure
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="setup">
          <GroupStructureSetup onStructureCreated={(group) => {
            setGroups([...groups, group]);
            setSelectedGroup(group);
            setActiveTab('consolidate');
          }} />
        </TabsContent>

        <TabsContent value="consolidate">
          <div className="space-y-6">
            {/* Group Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Group to Consolidate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Select 
                    value={selectedGroup?.group_id || ''} 
                    onValueChange={(v) => setSelectedGroup(groups.find(g => g.group_id === v))}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select a group structure" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map(group => (
                        <SelectItem key={group.group_id} value={group.group_id}>
                          {group.parent_name} ({group.reporting_year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleConsolidate} 
                    disabled={!selectedGroup || consolidating}
                  >
                    {consolidating ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <TrendingUp className="h-4 w-4 mr-2" />
                    )}
                    Run Consolidation
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {consolidationResults && (
              <ConsolidationResults results={consolidationResults} />
            )}

            {/* Selected Group Details */}
            {selectedGroup && !consolidationResults && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Group Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Parent Company</p>
                      <p className="font-medium">{selectedGroup.parent_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Methodology</p>
                      <p className="font-medium">{selectedGroup.control_methodology?.replace(/_/g, ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Entities</p>
                      <p className="font-medium">{selectedGroup.total_entities} entities</p>
                    </div>
                  </div>

                  {selectedGroup.entities?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium mb-3">Entities</h4>
                      <div className="space-y-2">
                        {selectedGroup.entities.map((entity, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Building2 className="h-4 w-4 text-gray-400" />
                              <span>{entity.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{entity.country}</Badge>
                              <Badge variant="secondary">{entity.ownership_percentage}%</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GroupConsolidation;
